import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Briefcase, ExternalLink, MapPin, MessageCircle, RotateCcw, Send, X, CheckCircle, Clock, AlertCircle } from 'lucide-react';
import './WebsiteChatWidget.css';

const STORAGE_KEY = 'merida_ai_platform_session_id';

const createSessionId = () => {
  if (window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  return `web-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const getRuntimeConfig = () => {
  const runtime = window.MERIDA_CHAT_WIDGET_CONFIG || {};
  return {
    apiUrl:
      runtime.apiUrl ||
      process.env.REACT_APP_AI_PLATFORM_URL ||
      'http://localhost:8002/platform/message',
    tenantId: runtime.tenantId || process.env.REACT_APP_AI_TENANT_ID || 'meridahr',
    brandId: runtime.brandId || process.env.REACT_APP_AI_BRAND_ID || 'website',
    agentId: runtime.agentId || process.env.REACT_APP_AI_AGENT_ID || 'default',
    title: runtime.title || process.env.REACT_APP_CHAT_WIDGET_TITLE || 'Merida AI Assistant',
    subtitle: runtime.subtitle || process.env.REACT_APP_CHAT_WIDGET_SUBTITLE || 'Online',
    launcherLabel: runtime.launcherLabel || process.env.REACT_APP_CHAT_LAUNCHER_LABEL || 'Chat',
    welcomeMessage:
      runtime.welcomeMessage ||
      process.env.REACT_APP_CHAT_WELCOME_MESSAGE ||
      `👋 Welcome to Merida HR. \n\n I'm Aria, your AI HR assistant.\n\nI'm here to help you find job opportunities, answer recruitment questions, and connect you with our HR team.\nHow may I help you today?`,
    primaryColor:
      runtime.primaryColor ||
      process.env.REACT_APP_CHAT_PRIMARY_COLOR ||
      '#1d68e5',
    secondaryColor:
      runtime.secondaryColor ||
      process.env.REACT_APP_CHAT_SECONDARY_COLOR ||
      '#16a34a',
    position: runtime.position || process.env.REACT_APP_CHAT_WIDGET_POSITION || 'right',
  };
};

const getSessionId = (forceNew = false) => {
  if (forceNew) {
    const newSessionId = createSessionId();
    sessionStorage.setItem(STORAGE_KEY, newSessionId);
    return newSessionId;
  }
  const existing = sessionStorage.getItem(STORAGE_KEY);
  if (existing) return existing;
  const sessionId = createSessionId();
  sessionStorage.setItem(STORAGE_KEY, sessionId);
  return sessionId;
};

// Search & Flow Keywords Helpers
const GENERIC_JOB_TERMS = [
  "looking for a job", "looking for job", "need a job", "need job",
  "looking for opportunities", "find me a job", "job search",
  "careers", "vacancies", "job openings", "jobs", "find job", "get a job",
  "explore careers", "openings", "hey there i'm looking for a job",
  "i am looking for a job", "find job opportunities"
];

const isGenericJobQuery = (text) => {
  const lower = text.toLowerCase().trim();
  return GENERIC_JOB_TERMS.some(term => lower.includes(term));
};

const SPECIFIC_ROLES = [
  "python developer", "graphic designer", "staff nurse", "hr executive",
  "java developer", "flutter developer", "react developer", "backend developer",
  "frontend developer", "node.js developer", "full stack developer", "nursing staff",
  "sales manager", "accountant", "data analyst", "digital marketer", "ui/ux designer",
  "software engineer", "devops engineer"
];

const detectSpecificRole = (text) => {
  const lower = text.toLowerCase().trim();
  return SPECIFIC_ROLES.find(role => lower.includes(role)) || null;
};

const STATUS_KEYWORDS = [
  "application status", "check application", "interview timing",
  "my interview date", "shortlisted", "interview status", "application update",
  "status of my application", "check status", "check my application status"
];

const isStatusQuery = (text) => {
  const lower = text.toLowerCase().trim();
  return STATUS_KEYWORDS.some(kw => lower.includes(kw));
};

const SIMILAR_JOBS_MAP = {
  "python developer": ["Backend Developer", "Django Developer", "Software Engineer", "API Developer"],
  "software development": ["Frontend Developer", "Full Stack Developer", "QA Engineer", "DevOps Engineer"],
  "java developer": ["Spring Boot Developer", "Backend Engineer", "Software Architect"],
  "flutter developer": ["React Native Developer", "iOS Developer", "Android Developer"],
  "graphic designer": ["UI/UX Designer", "Brand Designer", "Visual Communication Specialist"],
  "staff nurse": ["ICU Nurse", "Healthcare Assistant", "Clinical Specialist"],
  "hr executive": ["Recruiter", "Talent Acquisition Specialist", "HR Operations Generalist"],
  "healthcare": ["Staff Nurse", "Medical Lab Technician", "Patient Care Executive"],
  "sales": ["Business Development Executive", "Sales Manager", "Inside Sales Specialist"],
  "hr": ["HR Executive", "Recruitment Consultant", "HR Operations"],
  "finance": ["Accountant", "Financial Analyst", "Audit Associate"],
  "marketing": ["Digital Marketing Executive", "SEO Specialist", "Content Writer"]
};

const getApplyUrl = (job) => {
  if (job && job.title) {
    const desig = encodeURIComponent(job.title);
    const jobpk = encodeURIComponent(job.id || 0);
    return `https://hrm.meridahr.com/Canditate_Registration_Form?source=meridahr&desig=${desig}&jobpk=${jobpk}`;
  }
  return `https://hrm.meridahr.com/Canditate_Registration_Form?source=meridahr&desig=Other%20%2F%20General%20Applications&jobpk=0`;
};

const lookupApplicationStatus = (identifier) => {
  const clean = identifier.trim().toLowerCase();
  if (!clean) return null;

  const knownApplications = [
    {
      id: 'MHR-10492',
      email: 'vinod@example.com',
      name: 'Vinod Bajpayi',
      role: 'Software Engineer',
      status: 'Shortlisted',
      statusType: 'shortlisted',
      date: '2026-07-20'
    },
    {
      id: 'MHR-10493',
      email: 'priya@example.com',
      name: 'Priya Sharma',
      role: 'HR Executive',
      status: 'Interview Scheduled',
      statusType: 'interview',
      date: '2026-07-28 11:00 AM'
    },
    {
      id: 'MHR-10494',
      email: 'candidate@example.com',
      name: 'Amit Kumar',
      role: 'Python Developer',
      status: 'Under Review',
      statusType: 'review',
      date: '2026-07-25'
    }
  ];

  const match = knownApplications.find(
    app => app.id.toLowerCase() === clean || app.email.toLowerCase() === clean
  );

  if (match) return match;

  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  const idRegex = /^mhr-\d+$/i;

  if (emailRegex.test(clean) || idRegex.test(clean)) {
    return {
      id: clean.toUpperCase().includes('MHR') ? clean.toUpperCase() : `MHR-${Math.floor(10000 + Math.random() * 90000)}`,
      email: emailRegex.test(clean) ? clean : 'candidate@domain.com',
      name: 'Candidate',
      role: 'Registered Candidate',
      status: 'Application Received',
      statusType: 'received',
      date: new Date().toISOString().split('T')[0]
    };
  }

  return null;
};

function WebsiteChatWidget() {
  const config = useMemo(getRuntimeConfig, []);
  const [isOpen, setIsOpen] = useState(false);

  // STEP 1: Always pre-populate initial welcome message so it's instantly present at turn 1
  const [messages, setMessages] = useState(() => [
    {
      role: 'assistant',
      content: config.welcomeMessage,
      quick_replies: ["I'm looking for a job", 'Check my application status', 'Explore Careers'],
    },
  ]);

  const [input, setInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState('');

  // Guided Flow States: IDLE, AWAITING_DOMAIN, AWAITING_CATALOG, AWAITING_ALERT_PREF, AWAITING_STATUS_ID
  const [flowState, setFlowState] = useState('IDLE');
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [selectedCatalogType, setSelectedCatalogType] = useState(null);
  const [lastSearchedRole, setLastSearchedRole] = useState(null);

  // Modals & Candidates
  const [selectedJob, setSelectedJob] = useState(null);
  const [activePendingJob, setActivePendingJob] = useState(null);
  const [userCandidateData, setUserCandidateData] = useState(null);
  const [candidateForm, setCandidateForm] = useState({
    name: '',
    phone: '',
    email: '',
    domain: '',
    experience: 'Fresher',
    current_salary: '',
    expected_salary: ''
  });
  const [formError, setFormError] = useState('');
  const [expandedJobsState, setExpandedJobsState] = useState({});
  const scrollRef = useRef(null);

  const handleResetChat = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    getSessionId(true);
    setMessages([
      {
        role: 'assistant',
        content: config.welcomeMessage,
        quick_replies: ["I'm looking for a job", 'Check my application status', 'Explore Careers'],
      },
    ]);
    setError('');
    setFormError('');
    setSelectedJob(null);
    setActivePendingJob(null);
    setUserCandidateData(null);
    setFlowState('IDLE');
    setSelectedDomain(null);
    setSelectedCatalogType(null);
    setLastSearchedRole(null);
    setExpandedJobsState({});
  };

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isSending]);

  // Render Candidate Registration Form Inline inside Chat Stream
  const openCandidateForm = (job) => {
    setSelectedJob(null);
    const targetJob = job || { id: 0, title: '', is_unlisted: true };
    setActivePendingJob(targetJob);
    setFormError('');
    setCandidateForm({
      name: '',
      phone: '',
      email: '',
      domain: targetJob.title || '',
      experience: 'Fresher',
      current_salary: '',
      expected_salary: ''
    });

    setMessages((prev) => [
      ...prev,
      {
        role: 'assistant',
        content: `Please complete your candidate details in the short form below to register your interest:`,
        inline_form: {
          job_id: targetJob.id || 0,
          job_title: targetJob.title || '',
        },
      },
    ]);
  };

  // STEP 2 & Flow Handling - Send Message
  const handleSendMessage = async (textToSend, customMetadata = {}) => {
    const text = (textToSend || input).trim();
    if (!text || isSending) return;

    setError('');
    if (!textToSend) setInput('');

    // Append User Message to UI
    setMessages((prev) => [...prev, { role: 'user', content: text }]);

    // Directly execute SaaS AI API request
    executeApiJobSearch(text, customMetadata, text);
  };

  const executeApiJobSearch = async (text, customMetadata, originalRoleText) => {
    setIsSending(true);
    try {
      const response = await fetch(config.apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenant_id: config.tenantId,
          brand_id: config.brandId,
          agent_id: config.agentId,
          session_id: getSessionId(),
          message: text,
          channel: {
            channel: 'website',
            channel_session_id: getSessionId(),
            source_url: window.location.href,
            page_title: document.title,
            locale: navigator.language,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            user_agent: navigator.userAgent,
          },
          metadata: {
            path: window.location.pathname,
            referrer: document.referrer,
            ...customMetadata,
          },
        }),
      });

      if (!response.ok) {
        throw new Error(`AI platform returned ${response.status}`);
      }

      const data = await response.json();
      if (data.session_id) {
        sessionStorage.setItem(STORAGE_KEY, data.session_id);
      }

      const returnedJobs = Array.isArray(data.jobs) ? data.jobs : [];
      const hasUnlistedAction = Array.isArray(data.actions) && data.actions.some(act => act.type === 'open_unlisted_form');

      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: data.answer || 'How can I help you today?',
          quick_replies: data.quick_replies || [],
          jobs: returnedJobs,
          actions: data.actions || [],
        },
      ]);

      if (hasUnlistedAction) {
        openCandidateForm({ id: 0, title: originalRoleText, is_unlisted: true });
      }
    } catch (err) {
      setError('The assistant is temporarily unavailable. Please try again later.');
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: 'I am having trouble connecting right now.' },
      ]);
    } finally {
      setIsSending(false);
    }
  };

  const onSubmitForm = (e) => {
    e.preventDefault();
    handleSendMessage();
  };

  // STEP 4 Entry A: Candidate clicks "I'm Interested" on a job card
  const handleJobInterest = (job) => {
    setSelectedJob(null);
    openCandidateForm(job);
  };

  // STEP 4 & 5: Inline Candidate Registration Form Submit inside Chat Stream
  const submitInlineForm = (e, messageIndex, jobInfo) => {
    e.preventDefault();
    setFormError('');

    const name = candidateForm.name.trim();
    const phone = candidateForm.phone.trim();
    const email = candidateForm.email.trim();
    const domain = candidateForm.domain.trim();
    const experience = candidateForm.experience;
    const current_salary = candidateForm.current_salary.trim();
    const expected_salary = candidateForm.expected_salary.trim();

    if (!name || name.length < 2 || name.length > 50) {
      setFormError('Please enter your valid full name (min 2 characters).');
      return;
    }

    const phoneClean = phone.replace(/[^0-9]/g, '');
    if (!phoneClean || phoneClean.length !== 10 || !/^[6-9]\d{9}$/.test(phoneClean)) {
      setFormError('Please enter a valid 10-digit mobile number starting with 6, 7, 8, or 9.');
      return;
    }

    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!email || !emailRegex.test(email)) {
      setFormError('Please enter a valid email address.');
      return;
    }

    if (!domain) {
      setFormError('Please enter the position domain/title.');
      return;
    }

    if (!current_salary || isNaN(current_salary)) {
      setFormError('Please enter your current salary package (numeric).');
      return;
    }

    if (!expected_salary || isNaN(expected_salary)) {
      setFormError('Please enter your expected salary package (numeric).');
      return;
    }

    setUserCandidateData({ name, phone: phoneClean, email, domain, experience, current_salary, expected_salary });

    // Lock the inline form in history
    setMessages((prev) =>
      prev.map((msg, idx) =>
        idx === messageIndex
          ? {
              ...msg,
              inline_form_completed: true,
              candidate_details: { name, phone: phoneClean, email, domain, experience, current_salary, expected_salary },
            }
          : msg
      )
    );

    // Send the details to the API with metadata
    const visibleUserMessage = `Submitted Registration: ${name} | ${phoneClean} | ${email}`;
    
    handleSendMessage(visibleUserMessage, {
      form_type: 'candidate_quick_form',
      form_data: {
        name,
        phone: phoneClean,
        email,
        domain,
        experience,
        current_salary,
        expected_salary
      }
    });
  };

  const handleQuickReplyClick = (replyText) => {
    if (replyText.startsWith('Submit Details for')) {
      const role = replyText.replace('Submit Details for', '').trim();
      openCandidateForm({ id: 0, title: role, is_unlisted: true });
      return;
    }
    handleSendMessage(replyText);
  };

  const toggleExpandJobs = (msgIndex) => {
    setExpandedJobsState((prev) => ({ ...prev, [msgIndex]: !prev[msgIndex] }));
  };

  return (
    <div
      className={`website-chat-widget website-chat-widget--${config.position}`}
      style={{
        '--chat-primary': config.primaryColor,
        '--chat-secondary': config.secondaryColor,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: '16px', flexDirection: config.position === 'left' ? 'row-reverse' : 'row' }}>
        {isOpen && selectedJob && (
          <div className="website-chat-side-panel">
            <div className="website-chat-modal-header">
              <div>
                <h3>{selectedJob.title}</h3>
                <span className="website-chat-modal-company">{selectedJob.company || 'Merida HR'}</span>
              </div>
              <button
                type="button"
                className="website-chat-icon-button"
                onClick={() => setSelectedJob(null)}
              >
                <X size={18} />
              </button>
            </div>

            <div className="website-chat-modal-body">
              <div className="website-chat-modal-tags">
                <span className="website-chat-tag">
                  <MapPin size={13} /> {selectedJob.location}
                </span>
                <span className="website-chat-tag">
                  <Briefcase size={13} /> {selectedJob.experience}
                </span>
                {selectedJob.job_type && (
                  <span className="website-chat-tag">{selectedJob.job_type}</span>
                )}
                {selectedJob.salary && (
                  <span className="website-chat-tag highlight">{selectedJob.salary}</span>
                )}
              </div>

              {selectedJob.department && (
                <div className="website-chat-modal-section">
                  <h4>Department</h4>
                  <p>{selectedJob.department}</p>
                </div>
              )}

              {selectedJob.description && (
                <div className="website-chat-modal-section">
                  <h4>Job Description</h4>
                  <div
                    className="website-chat-job-desc-html"
                    dangerouslySetInnerHTML={{ __html: selectedJob.description }}
                  />
                </div>
              )}
            </div>

            <div className="website-chat-modal-footer">
              <button
                type="button"
                className="website-chat-btn-primary full-width"
                onClick={() => handleJobInterest(selectedJob)}
              >
                I'm interested
              </button>
            </div>
          </div>
        )}

        {isOpen && (
          <section className="website-chat-panel" aria-label="AI chat assistant">
            <header className="website-chat-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div className="website-chat-avatar">
                  <MessageCircle size={20} color="#ffffff" />
                </div>
                <div>
                  <h2>{config.title}</h2>
                  <p>{config.subtitle}</p>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  type="button"
                  className="website-chat-icon-button"
                  aria-label="Reset conversation"
                  title="Reset conversation"
                  onClick={handleResetChat}
                >
                  <RotateCcw size={18} />
                </button>
                <button
                  type="button"
                  className="website-chat-icon-button"
                  aria-label="Close chat"
                  onClick={() => setIsOpen(false)}
                >
                  <X size={20} />
                </button>
              </div>
            </header>

            <div className="website-chat-messages" ref={scrollRef}>
              {messages.map((message, index) => {
                const isAssistant = message.role === 'assistant';
                const hasJobs = isAssistant && Array.isArray(message.jobs) && message.jobs.length > 0;
                const hasActions = isAssistant && Array.isArray(message.actions) && message.actions.length > 0;
                const hasSimilar = isAssistant && Array.isArray(message.similar_jobs) && message.similar_jobs.length > 0;
                const hasStatusCard = isAssistant && message.status_card;
                const hasInlineForm = isAssistant && message.inline_form && !message.inline_form_completed;
                const isExpanded = !!expandedJobsState[index];
                const visibleJobs = hasJobs ? (isExpanded ? message.jobs : message.jobs.slice(0, 3)) : [];

                return (
                  <div key={`${message.role}-${index}`} className="website-chat-turn">
                    <div className={`website-chat-message website-chat-message--${message.role}`}>
                      {message.content}
                    </div>

                    {/* Render Inline Candidate Registration Form Card inside Chat Stream (matching SLA Chatbot style) */}
                    {hasInlineForm && (
                      <div className="website-chat-inline-form-card">
                        <div className="website-chat-inline-form-title">Your Details</div>

                        {formError && <div className="website-chat-error">{formError}</div>}

                        <form onSubmit={(e) => submitInlineForm(e, index, message.inline_form)}>
                          <div className="website-chat-inline-field">
                            <label>Full Name</label>
                            <input
                              type="text"
                              required
                              placeholder="Enter your full name"
                              value={candidateForm.name}
                              onChange={(e) => setCandidateForm({ ...candidateForm, name: e.target.value })}
                            />
                          </div>

                          <div className="website-chat-inline-field" style={{ marginTop: '10px' }}>
                            <label>Contact Number</label>
                            <input
                              type="tel"
                              required
                              maxLength={10}
                              placeholder="10-digit mobile number"
                              value={candidateForm.phone}
                              onChange={(e) =>
                                setCandidateForm({
                                  ...candidateForm,
                                  phone: e.target.value.replace(/[^0-9]/g, '').slice(0, 10),
                                })
                              }
                            />
                          </div>

                          <div className="website-chat-inline-field" style={{ marginTop: '10px' }}>
                            <label>Email Address</label>
                            <input
                              type="email"
                              required
                              placeholder="e.g. vinod@example.com"
                              value={candidateForm.email}
                              onChange={(e) => setCandidateForm({ ...candidateForm, email: e.target.value })}
                            />
                          </div>

                          <div className="website-chat-inline-field" style={{ marginTop: '10px' }}>
                            <label>Domain / Position Title</label>
                            <input
                              type="text"
                              required
                              placeholder="e.g. Software Engineer"
                              value={candidateForm.domain}
                              onChange={(e) => setCandidateForm({ ...candidateForm, domain: e.target.value })}
                            />
                          </div>

                          <div className="website-chat-inline-field" style={{ marginTop: '10px' }}>
                            <label>Experience Level</label>
                            <select
                              value={candidateForm.experience}
                              onChange={(e) => setCandidateForm({ ...candidateForm, experience: e.target.value })}
                              style={{
                                width: '100%',
                                padding: '8px',
                                borderRadius: '6px',
                                border: '1px solid #ccc',
                                background: '#fff'
                              }}
                            >
                              <option value="Fresher">Fresher</option>
                              <option value="Experienced">Experienced</option>
                              <option value="Career Gap">Career Gap</option>
                            </select>
                          </div>

                          <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                            <div className="website-chat-inline-field" style={{ flex: 1 }}>
                              <label>Current Salary (LPA)</label>
                              <input
                                type="number"
                                required
                                placeholder="e.g. 5"
                                value={candidateForm.current_salary}
                                onChange={(e) => setCandidateForm({ ...candidateForm, current_salary: e.target.value })}
                              />
                            </div>
                            <div className="website-chat-inline-field" style={{ flex: 1 }}>
                              <label>Expected Salary (LPA)</label>
                              <input
                                type="number"
                                required
                                placeholder="e.g. 8"
                                value={candidateForm.expected_salary}
                                onChange={(e) => setCandidateForm({ ...candidateForm, expected_salary: e.target.value })}
                              />
                            </div>
                          </div>

                          <button type="submit" className="website-chat-inline-submit-btn" style={{ marginTop: '15px' }}>
                            <Send size={15} />
                            <span>Continue</span>
                          </button>
                        </form>
                      </div>
                    )}

                    {/* Render STEP 8 Application Status Badge Card */}
                    {hasStatusCard && (
                      <div className="website-chat-status-card">
                        <div className="website-chat-status-header">
                          <span className="website-chat-status-id">{message.status_card.id}</span>
                          <span className={`website-chat-status-badge website-chat-status-badge--${message.status_card.statusType}`}>
                            {message.status_card.statusType === 'shortlisted' && <CheckCircle size={12} />}
                            {message.status_card.statusType === 'interview' && <Clock size={12} />}
                            {message.status_card.statusType === 'review' && <AlertCircle size={12} />}
                            {message.status_card.status}
                          </span>
                        </div>
                        <div className="website-chat-status-body">
                          <span className="website-chat-status-candidate">{message.status_card.name}</span>
                          <span className="website-chat-status-role">Applied Role: <strong>{message.status_card.role}</strong></span>
                          <span className="website-chat-status-date">Last Updated: {message.status_card.date}</span>
                        </div>
                      </div>
                    )}

                    {/* STEP 6: Render Apply Now Actions */}
                    {hasActions && (
                      <div className="website-chat-actions-container">
                        {message.actions.map((act, aIdx) => {
                          if (act.type === 'redirect_button' && act.payload?.url) {
                            return (
                              <a
                                key={`action-${aIdx}`}
                                href={act.payload.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="website-chat-apply-now-btn"
                              >
                                <span>{act.payload.label || 'Apply Now'}</span>
                                <ExternalLink size={16} />
                              </a>
                            );
                          }
                          return null;
                        })}
                      </div>
                    )}

                    {/* STEP 7: Render Recommended Similar Jobs */}
                    {hasSimilar && (
                      <div className="website-chat-recommendations">
                        <span className="website-chat-rec-title">Recommended Similar Roles:</span>
                        <div className="website-chat-rec-pills">
                          {message.similar_jobs.map((recTitle, rIdx) => (
                            <button
                              key={`rec-${rIdx}`}
                              type="button"
                              className="website-chat-rec-pill"
                              onClick={() => handleSendMessage(recTitle)}
                            >
                              + {recTitle}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Render Job Cards Container */}
                    {hasJobs && (
                      <div className="website-chat-jobs-container">
                        {visibleJobs.map((job, jIdx) => (
                          <div className="website-chat-job-card" key={`job-${job.id || jIdx}`}>
                            <div className="website-chat-job-header">
                              <h4 className="website-chat-job-title">{job.title}</h4>
                              {job.department && (
                                <span className="website-chat-job-dept-tag">{job.department}</span>
                              )}
                            </div>

                            <div className="website-chat-job-meta">
                              <span className="website-chat-job-location">
                                <MapPin size={13} /> {job.location}
                              </span>
                              {job.experience && (
                                <span className="website-chat-job-exp">
                                  <Briefcase size={13} /> {job.experience}
                                </span>
                              )}
                            </div>

                            <div className="website-chat-job-actions">
                              <button
                                type="button"
                                className="website-chat-btn-secondary"
                                onClick={() => setSelectedJob(job)}
                              >
                                View Details
                              </button>
                              <button
                                type="button"
                                className="website-chat-btn-primary"
                                onClick={() => handleJobInterest(job)}
                              >
                                I'm interested
                              </button>
                            </div>
                          </div>
                        ))}

                        {message.jobs.length > 3 && (
                          <button
                            type="button"
                            className="website-chat-view-more-btn"
                            onClick={() => toggleExpandJobs(index)}
                          >
                            {isExpanded ? 'Show less' : `View more (${message.jobs.length - 3} more)`}
                          </button>
                        )}
                      </div>
                    )}

                    {/* Quick Replies / Template Buttons */}
                    {isAssistant && Array.isArray(message.quick_replies) && message.quick_replies.length > 0 && (
                      <div className="website-chat-quick-replies">
                        {message.quick_replies.map((reply, rIdx) => (
                          <button
                            key={`qr-${rIdx}`}
                            type="button"
                            className="website-chat-quick-reply-btn"
                            onClick={() => handleQuickReplyClick(reply)}
                          >
                            {reply}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}

              {isSending && (
                <div className="website-chat-typing" aria-label="Assistant is typing">
                  <span />
                  <span />
                  <span />
                </div>
              )}
            </div>

            {error && <div className="website-chat-error">{error}</div>}

            <form className="website-chat-form" onSubmit={onSubmitForm}>
              <input
                type="text"
                value={input}
                onChange={(event) => setInput(event.target.value)}
                placeholder="Type your message"
                aria-label="Type your message"
                maxLength={2000}
              />
              <button type="submit" aria-label="Send message" disabled={!input.trim() || isSending}>
                <Send size={18} />
              </button>
            </form>
          </section>
        )}
      </div>

      <button
        type="button"
        className="website-chat-launcher"
        aria-label="Open chat assistant"
        onClick={() => setIsOpen((value) => !value)}
      >
        {isOpen ? <X size={24} /> : <MessageCircle size={24} />}
        <span>{config.launcherLabel}</span>
      </button>
    </div>
  );
}

export default WebsiteChatWidget;
